/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { Box } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import type { ElementType, ReactNode, WheelEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { APP_FONT_FAMILY } from '../../fonts';

interface MarkdownGuardedScrollBoxProps {
  children: ReactNode;
  contentSx?: SystemStyleObject<Theme>;
  contentTestId?: string;
  disabled?: boolean;
  scrollComponent?: ElementType;
  sx?: SystemStyleObject<Theme>;
}

const isMacPlatform = () =>
  typeof window !== 'undefined' && /Mac|iPhone|iPad|iPod/.test(window.navigator.platform);

function MarkdownGuardedScrollBox({
  children,
  contentSx,
  contentTestId,
  disabled = false,
  scrollComponent = 'div',
  sx,
}: Readonly<MarkdownGuardedScrollBoxProps>) {
  const scrollRef = useRef<HTMLElement | null>(null);
  const warningTimeoutRef = useRef<number | undefined>(undefined);
  const [showScrollWarning, setShowScrollWarning] = useState(false);

  useEffect(
    () => () => {
      if (warningTimeoutRef.current !== undefined) {
        window.clearTimeout(warningTimeoutRef.current);
      }
    },
    [],
  );

  const showGestureWarning = useCallback(() => {
    setShowScrollWarning(true);
    if (warningTimeoutRef.current !== undefined) {
      window.clearTimeout(warningTimeoutRef.current);
    }
    warningTimeoutRef.current = window.setTimeout(() => {
      setShowScrollWarning(false);
    }, 1700);
  }, []);

  const handleWheel = useCallback(
    (event: WheelEvent<HTMLElement>) => {
      if (disabled) return;

      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        event.stopPropagation();
        setShowScrollWarning(false);
        if (scrollRef.current) {
          scrollRef.current.scrollTop += event.deltaY;
          scrollRef.current.scrollLeft += event.deltaX;
        }
        return;
      }

      showGestureWarning();
    },
    [disabled, showGestureWarning],
  );

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <Box
        component={scrollComponent}
        data-testid={contentTestId}
        onWheel={handleWheel}
        ref={scrollRef}
        sx={{ overflow: disabled ? 'auto' : 'hidden', ...contentSx }}
      >
        {children}
      </Box>
      {showScrollWarning && (
        <Box
          aria-hidden="true"
          sx={{
            '@keyframes pa-agent-gestures-fadein': {
              '0%': { opacity: 0 },
              '100%': { opacity: 1 },
            },
            animation: 'pa-agent-gestures-fadein 0.8s forwards',
            alignItems: 'center',
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            bottom: 0,
            color: '#fff',
            display: 'flex',
            fontFamily: APP_FONT_FAMILY,
            fontSize: 22,
            justifyContent: 'center',
            left: 0,
            p: '15px',
            pointerEvents: 'none',
            position: 'absolute',
            right: 0,
            textAlign: 'center',
            top: 0,
            zIndex: 461,
          }}
        >
          {isMacPlatform()
            ? 'Use ⌘ + scroll to scroll this block'
            : 'Use Ctrl + scroll to scroll this block'}
        </Box>
      )}
    </Box>
  );
}

export default MarkdownGuardedScrollBox;
